import { request as apiRequest, fetchWithAuthRetry, API_BASE_URL } from '@/lib/apiClient'
import { chunkedUpload } from '@/lib/chunkedUpload'
import type { ScreeningReviewGroup } from './screening/reviewGroups'

// #3: parse-assessment uploads above this size use the chunked path so
// each request clears Cloudflare Free's ~100 s edge timeout. 8 MB matches
// the existing soft-warning threshold
const ASSESSMENT_CHUNK_THRESHOLD_BYTES = 8 * 1024 * 1024

/**
 * Error returned by the JSON API. It remains an Error (and keeps the
 * familiar `.message`) for existing callers, while preserving the status
 * and structured response body for flows such as RA draft cost/gate
 * refusals.
 */
export class ApiError<TBody = unknown> extends Error {
  readonly status?: number
  readonly body?: TBody

  constructor(message: string, status?: number, body?: TBody) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export async function apiFetch<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const { data, error } = await apiRequest<T>(method, path, body)
  if (error) throw new ApiError(error.message, error.status, error.body)
  return data as T
}

export interface Shipyard {
  id:   number
  code: string
  name: string
  // present since the shipyard-logo migration; optional so older cached
  // payloads (and the create-project response path) still typecheck
  is_active?:       boolean
  has_logo?:        boolean
  logo_updated_at?: string | null
}

/**
 * Public URL for a yard's uploaded logo, or undefined when it has none (the
 * caller falls back to the bundled asset). The upload timestamp rides along as
 * a cache buster so a replaced logo shows up immediately instead of after the
 * 5-minute max-age.
 */
export function shipyardLogoUrl(
  s: Pick<Shipyard, 'id' | 'has_logo' | 'logo_updated_at'> | null | undefined
): string | undefined {
  if (!s?.has_logo) return undefined
  const v = s.logo_updated_at ? Date.parse(s.logo_updated_at) : 0
  return `${API_BASE_URL}/api/public/shipyard-logo/${s.id}?v=${Number.isFinite(v) ? v : 0}`
}

export interface Regulation {
  id:   number
  code: string
  name: string
}

export type ComplianceAuthorityStatus = 'verified' | 'template_only'

export interface ComplianceRuleProfile {
  profile_code: string
  class_society: 'DNV' | 'BV' | 'ABS' | 'LR' | 'KR' | 'NK' | 'RINA'
  display_name: string
  edition_label: string
  edition_date: string
  notation_label: string
  rule_reference: string
  template_reference: string
  authority_status: ComplianceAuthorityStatus
  e22_revision: string
  e26_revision: string
  e27_revision: string
}

export interface ProjectComplianceProfile extends ComplianceRuleProfile {
  id?: string
  position: 1 | 2
  notation_qualifier: string | null
  selected_at?: string
}

export interface ComplianceProfileSelection {
  profile_code: string
  notation_qualifier?: string | null
}

// reusable, already-assessed system from the library (any project)
export interface RaLibrarySystem {
  source_assessment_id: string
  system_name:          string
  supplier:             string | null
  bucket:               string
  ship_function:        string | null
  source_hull:          string | null
  source_society:       string | null
  has_deepdive:         boolean
  completeness:         number | null   // 0..10, how much deep-dive data is filled (null if no deep-dive)
  is_complete:          boolean          // strict: every RA section's values are filled (safe to generate)
}

export interface Project {
  id:             string
  shipyard_id:    number | null
  shipyard_name:  string | null
  shipyard_code:  string | null
  project_name:   string | null
  hull_number:    string
  imo_number:     string | null
  mmsi?:          string | null
  delivered_at?:  string | null
  vessel_name:    string | null
  vessel_type:    string | null
  ship_owner:     string | null
  status:         string
  // project's class society (enum: DNV/LR/ABS/BV/KR/NK/RINA/RS/CCS).
  // drives F2 sort + scope classification. persisted from the
  // "Classification Society" picker on create/edit, back-filled by the
  // Word-RA auto-detect when left unset
  class_society?:  string | null
  construction_contract_date?: string | null
  compliance_profiles?: ProjectComplianceProfile[]
  system_count:   number
  import_count:   number
  regulation_codes: string[]
  created_at:     string
  updated_at:     string
}

export interface ProjectDetail extends Project {
  regulations: Regulation[]
  batches:     ImportBatch[]
  compliance_profiles: ProjectComplianceProfile[]
}

export interface NmsConfiguration {
  id:                  string
  project_id:          string
  original_filename:   string
  normalized_filename: string
  relative_path:       string | null
  device_name:         string | null
  ip_address:          string | null
  config_label:        string | null
  mime_type:           string
  file_size_bytes:     number
  content_sha256:      string
  revision_number:     number
  is_current:          boolean
  supersedes_id:       string | null
  uploaded_by:         string | null
  uploader_email:      string | null
  created_at:          string
  hull_number:         string
  vessel_name:         string | null
  project_name:        string | null
  shipyard_name:       string | null
}

export interface NmsUploadResult {
  configuration: NmsConfiguration
  duplicate: boolean
}

export interface CreateProjectInput {
  shipyard_id: number | null
  project_name: string | null
  hull_number: string
  imo_number: string | null
  vessel_name: string | null
  vessel_type: string | null
  ship_owner: string | null
  status?: string
  construction_contract_date?: string | null
  compliance_profiles: ComplianceProfileSelection[]
  regulation_ids?: number[]
}

export type UpdateProjectInput = Partial<Omit<CreateProjectInput, 'hull_number'>> & {
  hull_number?: string
  mmsi?: string | null
  delivered_at?: string | null
}

// SEACURE(TOOL) installer release. one row per (version, edition),
// editions told apart by `installer_file_name` substring rather than
// a dedicated column. `is_split=true` releases are stored as a sequence
// of chunks in `release_file_chunks`, the dedicated download endpoint
// reassembles them, callers don't need to know
export interface ReleaseRow {
  id:                  number
  version:             string
  installer_file_name: string
  installer_file_size: number
  is_compressed:       boolean | null
  original_file_size:  number | null
  is_split:            boolean | null
  total_chunks:        number | null
  release_notes:       string | null
  created_at:          string
  updated_at:          string
}

export interface ImportBatch {
  id:                string
  project_id:        string
  import_type:       string
  original_filename: string | null
  sheet_name:        string | null
  status:            'draft' | 'committed' | 'cancelled'
  row_count:         number
  created_at:        string
  committed_at:      string | null
}

export interface ImportBatchRow {
  id:                string
  batch_id:          string
  row_no:            number
  raw_data:          Record<string, unknown> | null
  parsed_data:       ParsedImportRow | null
  validation_errors: string[] | null
  is_valid:          boolean
}

export interface ImportContact {
  full_name?: string
  email?:     string
  phone?:     string
}

export interface ParsedImportRow {
  function_name?:        string
  system_description?:   string
  vendor_name_raw?:      string
  system_name?:          string
  type_approval_status?: string
  // A/B/C grade from Vendor Tracking format imports. null for System Register
  // imports (no grade column there)
  cbs_grade?:            string | null
  contact_full_name?:    string
  contact_email?:        string
  contact_phone?:        string
  // multi-contact rows
  contacts?:      ImportContact[]
  multi_contact?: boolean
}

export interface ImportBatchDetail extends ImportBatch {
  rows: ImportBatchRow[]
}

export interface StagePayload {
  project_id:        string
  original_filename: string
  sheet_name:        string
  // optional, defaults to 'system_register' on the server. Vendor Tracking
  // imports must pass 'vendor_tracking' so the batch is tagged right
  import_type?:      'system_register' | 'vendor_tracking'
  rows: Array<{
    row_no:            number
    raw_data:          Record<string, unknown>
    parsed_data:       ParsedImportRow
    validation_errors: string[] | null
    is_valid:          boolean
  }>
}

export interface CommitResult {
  systems_created:  number   // no match → inserted new
  systems_updated:  number   // matched existing → updated in place (id stable)
  junk_skipped:     number   // rows with no system identity, not imported
  junk_rows:        number[] // their workbook row numbers
  contacts_created: number
  contacts_reused:  number
  links_created:    number
}

export type RaCompletion = 'not_started' | 'partial' | 'done'

export type ProjectSystemSource = 'excel' | 'word_ra' | 'split'

export interface ProjectSystem {
  id:                    string
  function_name:         string | null
  system_description:    string | null
  vendor_name_raw:       string | null
  system_name:           string | null
  type_approval_status:  string | null
  cbs_grade:             string | null
  system_profile_id:     string | null
  ra_completion_status?: RaCompletion
  // F1 per-document presence marks, pure presence, no dates. replace
  // the NEW/WIP/DONE badge with three filled/hollow marks
  has_word_ra?:          boolean   // Word-RA assessment mapped here
  has_asset_inv?:        boolean   // systems_190 rows OR captured DNV inventory_list
  has_excel_ra?:         boolean   // legacy RA or system-profile (CBS) assessment
  // requirement statuses — the completeness model (substitutable sources).
  // drives the RA / INV row marks, the filter, and the checklist summary
  ra_status?:            RaRequirementStatus
  inventory_status?:     InventoryRequirementStatus
  requirements_met?:     boolean
  // F2 regulatory function ordering. list comes back pre-sorted by
  // the backend (mapped → regulation order, unmapped after). sort_function
  // is the function to GROUP under: the Word RA function when mapped (so
  // mislabeled Excel rows surface under their real function), else Excel
  sort_function?:        string | null
  sort_display?:         string | null   // canonical group header (collapses name variants at one position)
  function_sort_order?:  number | null
  // Item A: scope verdict aggregated from this system's linked asset-
  // inventory rows. 'out_of_scope' rows render in a trailing group,
  // 'uncertain' shows a marker (AI unsure), null = not yet classified.
  // scope_function is the proposed regulatory function (already folded
  // into the F2 sort for in_scope rows)
  scope_status?:         'in_scope' | 'out_of_scope' | 'uncertain' | null
  scope_function?:       string | null
  contact_count:         number
  created_at:            string
  // Word RA authority Phase 1 (migration 028) + Phase 3 listing change.
  // 'excel' for inventory-imported rows (the historical default),
  // 'word_ra' for Phase 2 CREATE rows, 'split' for clones from the
  // SPLIT endpoint. the Phase 3 dropdowns badge non-excel rows so
  // users can tell at a glance where each option came from
  source:                ProjectSystemSource
}

export interface GlobalContact {
  id:            string
  full_name:     string | null
  email:         string | null
  phone:         string | null
  company_name:  string | null
  /** the shipyard's own owner(s) for this contact — SHI forms only */
  shipyard_contacts?: string[]
  shipyard_contact_emails?: string[]
  created_at:    string
  updated_at:    string
  linked_systems: Array<{
    system_id:     string
    system_name:   string | null
    function_name: string | null
    vendor:        string | null
    project_id:    string
  }>
  linked_projects: Array<{
    project_id:    string
    hull_number:   string
    shipyard_name: string | null
    vessel_name:   string | null
  }>
}

export interface SystemListItem {
  id: string
  project_id: string
  function_name: string | null
  system_name: string | null
  system_description: string | null
  vendor_name_raw: string | null
  type_approval_status: string | null
  system_profile_id: string | null
  hull_number: string
  vessel_name: string | null
  shipyard_name: string | null
  profile_vendor: string | null
  profile_model: string | null
  is_cbs: boolean | null
  cbs_imported_at: string | null
  assessment_status: 'not_assessed' | 'cbs' | 'non_cbs'
}

export interface CbsAnswers {
  q1: 'Y' | 'N' | null
  q2: 'Y' | 'N' | null
  q3: 'Y' | 'N' | null
  q4: 'Y' | 'N' | null
  q5: 'Y' | 'N' | null
  q6: 'Y' | 'N' | null
  q1_evidence_note?: string
  q2_evidence_note?: string
  q3_evidence_note?: string
  q4_evidence_note?: string
  q5_evidence_note?: string
  q6_evidence_note?: string
}

export interface VendorResponse {
  question_code: string
  category: string
  question_text: string
  dropdown_response: string | null
  detailed_response: string | null
  remark: string | null
}

export interface ParsedAssessment {
  header: {
    yard_hull: string | null
    system_name: string | null
    vendor_name: string | null
    model_name: string | null
  }
  cbs_answers: CbsAnswers
  is_cbs: boolean
  vendor_responses: VendorResponse[]
}

export interface ProposedProfile {
  vendor_name: string
  model_name: string
  system_name: string
  normalized_key: string
}

export interface ExistingProfileMatch {
  id: string
  vendor_name: string
  model_name: string
  system_name: string
  linked_count: number
  has_cbs: boolean
  has_responses: boolean
  // backend-computed auto-decision: identical upload (or empty profile) →
  // reuse without asking; differing content → separate variant profile
  contents_match: boolean
  profile_empty: boolean
  auto_reuse: boolean
}

export interface VaiPreview {
  vessel_header: {
    vessel_name:      string | null
    imo_number:       string | null
    class_society_id: string | null
    shipyard_name:    string | null
    date:             string | null
  }
  rows:      AssetInventoryRow[]
  row_count: number
}

export interface ParseAssessmentResultNew {
  format: 'new'
  parsed: ParsedAssessment
  proposed_profile: ProposedProfile
  existing_profile: ExistingProfileMatch | null
  project_system: {
    id: string
    current_profile_id: string | null
    vendor_name_raw: string | null
    system_name: string | null
    project_id: string | null
  }
  vai_preview:     VaiPreview | null
  vai_parse_error: string | null
}

export interface LegacyRaHeader {
  company_name:           string | null
  target_system:          string | null
  company_email:          string | null
  company_tel:            string | null
  vendor_pic_name:        string | null
  vendor_pic_grade:       string | null
  vendor_pic_email:       string | null
  vendor_pic_tel:         string | null
  onboard_engineer_name:  string | null
  onboard_engineer_grade: string | null
  onboard_engineer_email: string | null
  onboard_engineer_tel:   string | null
}

export interface LegacyRaRow {
  ra_function:                string | null
  ra_function_of_system:      string | null
  ra_vendor:                  string | null
  cbs_interpretation_1:       string | null
  cbs_interpretation_2:       string | null
  acceptance_criteria_result: string | null
  ac_1:                       string | null  // workbook "1)"
  ac_2:                       string | null  // workbook "2)"
  ac_3_1:                     string | null  // workbook "3)-1"
  ac_3_2:                     string | null  // workbook "3)-2"
  ac_3_3:                     string | null  // workbook "3)-3"
  ac_4:                       string | null  // workbook "4)"
  type_approval_status:       string | null
  additional_1:               string | null
  additional_2:               string | null
  additional_3:               string | null
  iacs_impact:                string | null
  iacs_complexity:            string | null
  iacs_connectivity:          string | null
  iacs_attack_surface:        string | null
  iacs_risk_level:            string | null
}

export interface LegacyRaRowScored extends LegacyRaRow {
  score: number
}

export interface ParseAssessmentResultLegacy {
  format: 'legacy'
  header: LegacyRaHeader
  scored_rows: LegacyRaRowScored[]
  source_filename: string
  project_id?:     string | null
  vai_preview?:    VaiPreview | null
  vai_parse_error?: string | null
}

// ── Word RA types (Phase A) ─────────────────────────────────────────────────

export type RaBucket = 'target' | 'exclusion' | 'non_target' | 'ip_based' | 'pending'

export interface ParsedRaHeader {
  project_name:     string | null
  vessel_name:      string | null
  class_society:    string | null
  applicable_rules: string[]
  revision:         string | null
}

export interface ParsedRaMasterRow {
  bucket:        RaBucket
  ordering_index: number
  system_name:   string
  supplier:      string | null
  ship_function: string | null
  essential_1:   string | null
  essential_2:   string | null
  essential_3:   string | null
  essential_4:   string | null
  cat:           string | null
  l_grade:       number | null
  p_grade:       number | null
  rl_value:      number | null
  result_label:  string | null
}

export interface ParsedRaDetail {
  system_name:                 string
  supplier:                    string | null
  identification:              Record<string, string | null> | null
  acceptance_criteria:         Array<{ requirement: string; compliance_status: string | null; explanation: string | null }> | null
  characteristics:             Record<string, string | null> | null
  architecture:                { description: string | null; hardware: Array<{ name: string; spec: string }> } | null
  cat_grade:                   string | null
  cat_explanation:             string | null
  cx_grade:                    string | null
  cx_explanation:              string | null
  cx_inputs:                   Record<string, string | null> | null
  cy_grade:                    string | null
  cy_explanation:              string | null
  cy_decision_path:            string | null
  us_grade:                    string | null
  at_grade:                    string | null
  h_grade:                     string | null
  h_explanation:               string | null
  as_grade:                    string | null
  likelihood_grade:            number | null
  impact_c:                    number | null
  impact_i:                    number | null
  impact_a:                    number | null
  impact_t:                    number | null
  impact_explanations:         Record<'c' | 'i' | 'a' | 't', string | null> | null
  impact_grade:                number | null
  risk_level:                  number | null
  treatment_decision:          string | null
  existing_org_controls:       string | null
  existing_physical_controls:  string | null
  additional_measures:         string | null
  residual_risk_statement:     string | null
  final_statement:             string | null
}

export interface ParsedRa {
  header:           ParsedRaHeader
  master_table:     ParsedRaMasterRow[]
  excluded_details: ParsedRaDetail[]
  parse_warnings:   string[]
}

export type RaMatchConfidence = 'auto' | 'review' | 'none'

export interface RaMatchCandidate {
  project_system_id: string
  score:             number
  reasoning:         string
}

export interface RaMatchSuggestion {
  ra_system_index:   number
  project_system_id: string | null
  confidence:        RaMatchConfidence
  candidates:        RaMatchCandidate[]
}

export interface ParseRaDocumentResult {
  session_id:              string
  parsed:                  ParsedRa
  project:                 { id: string; project_name: string | null; vessel_name: string | null; class_society: string | null }
  suggested_class_society: string | null
  source_filename:         string
  match_suggestions:       RaMatchSuggestion[]
}

// ── AI drawing screening rows (Phase 5) ─────────────────────────────────

export interface ScreeningRunRow {
  id:                string
  project_id:        string
  model:             string
  prompt_version:    string | null
  status:            'processing' | 'review' | 'promoted' | 'failed'
  prompt_tokens:     string | number
  completion_tokens: string | number
  cost_usd:          string | number
  systems_total:     number
  systems_done:      number
  error_message:     string | null
  created_at:        string
  updated_at:        string
  drawing_count?:    number
  system_counts?:    Record<string, number> | null
}

export interface ScreeningDrawingRow {
  id:                string
  original_filename: string | null
  file_size_bytes:   number
  page_count:        number | null
  status:            string
  fail_reason:       string | null
  unmapped_pages:    number[] | null
}

export interface ScreeningEvidenceItem {
  page:             number
  cable_type:       string | null
  interface_device: string | null
  quote:            string
}

export type ScreeningIpBasis = 'explicit_ip' | 'explicit_serial' | 'explicit_hardwired' | 'internal_ip_only' | 'no_data_connections' | 'unclassifiable'

export interface ScreeningSystemRow {
  /** Read-only projection; actions and evidence always use raw member rows. */
  review_group?:      ScreeningReviewGroup
  id:                 string
  run_id:             string
  drawing_id:         string | null
  item_no:            number | null
  raw_name:           string | null
  system_name:        string
  model:              string | null
  maker:              string | null
  cbs:                string | null
  ship_function:      string | null
  detail:             string | null
  ip:                 string | null
  ip_basis:           ScreeningIpBasis | null
  scope_class:        'system' | 'component' | 'infrastructure' | null
  connected_system:   string | null
  target:             string | null
  remark:             string | null
  page_start:         number | null
  // contacts a shipyard has answered for, waiting for this row to be
  // promoted into the register
  pending_contacts?:  Array<{ id: string; full_name: string | null; email: string | null; phone: string | null; company_name: string | null }>
  page_end:           number | null
  circ_mark:          string | null
  owner_supplied:     boolean
  is_system:          boolean
  status:             'proposed' | 'confirmed' | 'rejected' | 'edited' | 'failed' | 'pending_maker_drawing'
  mismatch:           boolean
  title_block_system: string | null
  fail_reason:        string | null
  contents_source?:   'index' | 'headers' | 'both'
  anchor_note:        string | null
  reject_reason:      string | null
  ai_original:        Record<string, unknown> | null
  promoted_project_system_id: string | null
  evidence:           ScreeningEvidenceItem[]
  confidence:         number | null
  confirmed_by:       string | null
  confirmed_at:       string | null
}

export interface RaDocumentRow {
  id:               string
  project_id?:      string
  class_society:    string | null
  compliance_profile_code?: string | null
  revision:         string | null
  vessel_name:      string | null
  project_name:     string | null
  applicable_rules: string[]
  parsed_at:        string
  parsed_by?:       string | null
  created_at:       string
}

// one project deliverable for the Documents tab. `available` is data-driven
// (present + complete); `reason` explains a Missing state (mirrors the RA
// generate route's fail-loud messaging).
export interface ProjectDeliverable {
  key:             string          // 'ra' | 'asset_inventory' | (future)
  label:           string
  available:       boolean
  reason:          string | null
  // soft-gate deliverables (asset inventory): non-blocking data-quality note
  // shown alongside Available, e.g. "12 of 67 items incomplete — …"
  warning?:        string | null
  detail:          string | null   // e.g. "ABS · 9 systems", "42 items"
  ra_document_id?: string | null   // download source for the RA deliverable
  generator_ready?: boolean        // false until the formatted generator lands
}

export type RaGenerationStatus =
  | 'queued'
  | 'preparing'
  | 'review_required'
  | 'finalizing'
  | 'ready'
  | 'failed'
  | 'cancelled'

export interface RaGenerationProfile {
  profile_code: string
  class_society: string
  display_name?: string
  position?: number
}

export interface RaGenerationReadiness {
  total_systems: number
  routings: Record<string, number>
  can_start: boolean
  blockers: string[]
  rows?: Array<{
    screening_system_id: string
    project_system_id: string | null
    system_name: string
    routing: string
    reasons: string[]
    warnings: string[]
  }>
}

export interface RaGenerationItem {
  id?: string
  system_name: string
  routing: string
  status: string
  error_message: string | null
  ra_draft_id: string | null
}

export interface RaGenerationRun {
  id: string
  project_id?: string
  profile_code?: string
  status: RaGenerationStatus
  max_cost_usd?: number | string | null
  actual_cost_usd?: number | string | null
  error_message?: string | null
  ra_document_id?: string | null
  created_at?: string
  updated_at?: string
  queue_position?: number | null
  total_systems?: number
  processed_systems?: number
  current_system_name?: string | null
  progress?: {
    completed: number
    total: number
    percent: number
    current_system_name: string | null
  }
  items: RaGenerationItem[]
}

export interface RaInstructionResult {
  id: string
  status: 'completed' | 'needs_clarification'
  summary: string
  clarification: string
  applied: Array<{
    id: string
    system_name: string
    cbs: string | null
    cbs_source: string
    scope_class: string
    target: string | null
    status: string
  }>
}

export interface RaGenerationState {
  profile: RaGenerationProfile | null
  profiles?: RaGenerationProfile[]
  readiness: RaGenerationReadiness
  run?: RaGenerationRun | null
  active_run?: RaGenerationRun | null
  latest_run?: RaGenerationRun | null
}

// per-system requirement statuses — THE completeness model. two requirements,
// each satisfied by substitutable sources (shared SQL predicates on the
// server, so this always agrees with the dashboard rollup).
export type RaRequirementStatus = 'done' | 'partial' | 'missing'
export type InventoryRequirementStatus = 'done' | 'missing'

export interface ProjectRequirements {
  total: number      // active systems
  complete: number   // both requirements met
  ra: { done: number; partial: number; missing: number }
  inventory: { done: number; missing: number }
  systems: {
    id: string
    name: string | null
    ra_status: RaRequirementStatus
    inventory_status: InventoryRequirementStatus
    complete: boolean
  }[]
}

export interface RaSystemAssessmentRow {
  id:                  string
  ra_document_id:      string
  project_system_id:   string | null
  bucket:              RaBucket
  ordering_index:      number
  system_name:         string
  supplier:            string | null
  ship_function:       string | null
  essential_1:         string | null
  essential_2:         string | null
  essential_3:         string | null
  essential_4:         string | null
  cat:                 string | null
  l_grade:             number | null
  p_grade:             number | null
  rl_value:            number | null
  result_label:        string | null
  system_profile_id:   string | null
  created_at:          string
}

export interface AutoMapSuggestion {
  assessment_id:               string
  suggested_project_system_id: string | null
  confidence:                  'high' | 'medium' | 'low'
  reasoning:                   string
  tools_used:                  string[]
}

export interface AutoMapSessionStatus {
  id:                       string
  ra_document_id:           string
  project_id:               string
  state:                    'running' | 'complete' | 'failed' | 'cancelled'
  started_at:               number
  completed_at:             number | null
  total_assessments:        number
  processed_count:          number
  current_assessment_id:    string | null
  tool_calls_made:          number
  prompt_tokens_total:      number
  completion_tokens_total:  number
  estimated_cost_usd:       number
  suggestions:              AutoMapSuggestion[]
  error:                    string | null
}

// Phase E1.5: persisted suggestion lifecycle row, returned by the
// GET /api/ra-documents/:id/ai-suggestions endpoint. joined with the
// assessment so the AI Suggestions pane has system_name / supplier
// without a second fetch
export type AiSuggestionState = 'pending' | 'accepted' | 'rejected' | 'dismissed' | 'superseded'

export interface AiSuggestionRow {
  id:                          string
  session_id:                  string
  assessment_id:               string
  suggested_project_system_id: string | null
  confidence:                  'high' | 'medium' | 'low'
  reasoning:                   string
  tools_used:                  string[]
  state:                       AiSuggestionState
  acted_at:                    string | null
  acted_by:                    string | null
  created_at:                  string
  assessment_system_name:      string
  assessment_supplier:         string | null
  // Phase 3: surfaces the master-table ship_function so the
  // CreateProjectSystemModal can pre-fill the editable function_name
  // field. may be NULL (e.g. Sewage Treatment in SN2729)
  assessment_ship_function:    string | null
  assessment_bucket:           string
  // non-null when ANOTHER assessment in the same ra_document is already
  // mapped to suggested_project_system_id. accepting this suggestion
  // would 409 against uq_ra_sa_psid_per_doc. the FE uses this to render
  // a disabled-Accept badge + "Conflicts with: <name>" chip so the user
  // sees the collision before clicking
  target_conflict:             string | null
}

// distinct 409-code constants from POST /ai-suggestions/:id/accept.
// mirror the server-side SUGGESTION_ACCEPT_CODES in raDocuments.ts so
// the FE branches on err.code rather than substring-matching the
// human message. strings (not enums) so the wire shape is stable
export type AcceptSuggestionErrorCode = 'ASSESSMENT_SLOT_TAKEN' | 'SUGGESTION_ALREADY_ACTED'

// thrown by acceptAiSuggestion on any non-2xx. carries the parsed
// body so the UI can render the named-conflict message + decide
// which 409 branch to take (slot-taken vs already-acted) without
// re-parsing the response. message is always populated, code +
// conflicting_assessment only set when the server includes them
export interface AcceptSuggestionError extends Error {
  status?:                 number
  code?:                   AcceptSuggestionErrorCode
  conflicting_assessment?: { id: string; system_name: string } | null
}

// Phase 3: typed errors for the Word RA authority endpoints added
// in commit 8921bee. mirror SUGGESTION_ACCEPT_CODES's wire-contract
// pattern: the FE branches on err.code without touching the human
// message. WORD_RA_AUTHORITY_CODES lives server-side, this
// type just enumerates the four code strings
export type WordRaAuthorityErrorCode =
  | 'ALREADY_MAPPED'
  | 'DUPLICATE_SYSTEM'
  | 'NAME_TAKEN'
  | 'CROSS_PROJECT'

export interface SplitProjectSystemError extends Error {
  status?: number
  code?:   'ALREADY_MAPPED' | 'NAME_TAKEN' | 'CROSS_PROJECT'
}

export interface CreateProjectSystemError extends Error {
  status?: number
  code?:   'ALREADY_MAPPED' | 'DUPLICATE_SYSTEM'
  // DUPLICATE_SYSTEM only: the existing row the caller can map to
  // instead. shape kept compact since the Create modal only needs
  // id + name + vendor + source to render the "Map to this instead"
  // affordance, full ProjectSystem data is overkill
  existing_project_system?: {
    id:              string
    system_name:     string | null
    vendor_name_raw: string | null
    source:          ProjectSystemSource
  } | null
}

export interface RaDiagramFile {
  id:                string
  kind:              'topology' | 'wiring' | 'other'
  mime_type:         string
  file_size_bytes:   number
  content_hash:      string
  ordering_index:    number
  original_filename: string | null
}

export interface SystemWordRaResponse {
  ra_system_assessment: {
    id:              string
    ra_document_id:  string
    bucket:          RaBucket
    ordering_index:  number
    system_name:     string
    supplier:        string | null
    ship_function:   string | null
    essential_1:     string | null
    essential_2:     string | null
    essential_3:     string | null
    essential_4:     string | null
    cat:             string | null
    l_grade:         number | null
    p_grade:         number | null
    rl_value:        number | null
    result_label:    string | null
  }
  ra_excluded_detail: RaExcludedDetailRow | null
  diagrams:           RaDiagramFile[]
  ra_document_meta: {
    class_society: string | null
    vessel_name:   string | null
    revision:      string | null
    parsed_at:     string
  }
}

export interface RaExcludedDetailRow {
  id:                          string
  ra_system_assessment_id:     string
  identification:              Record<string, string | null> | null
  acceptance_criteria:         Array<{ requirement: string; compliance_status: string | null; explanation: string | null }> | null
  characteristics:             Record<string, string | null> | null
  architecture:                { description: string | null; hardware: Array<{ name: string; spec: string }> } | null
  cat_grade:                   string | null
  cat_explanation:             string | null
  cx_grade:                    string | null
  cx_explanation:              string | null
  cx_inputs:                   Record<string, string | null> | null
  cy_grade:                    string | null
  cy_explanation:              string | null
  cy_decision_path:            string | null
  us_grade:                    string | null
  at_grade:                    string | null
  h_grade:                     string | null
  h_explanation:               string | null
  as_grade:                    string | null
  likelihood_grade:            number | null
  impact_c:                    number | null
  impact_i:                    number | null
  impact_a:                    number | null
  impact_t:                    number | null
  impact_explanations:         Record<'c' | 'i' | 'a' | 't', string | null> | null
  impact_grade:                number | null
  risk_level:                  number | null
  treatment_decision:          string | null
  existing_org_controls:       string | null
  existing_physical_controls:  string | null
  additional_measures:         string | null
  residual_risk_statement:     string | null
  final_statement:             string | null
  // Section 4.3 (Verification of Non-CBSs) fields, populated when the
  // parent assessment's bucket is 'non_target'. both surface together.
  // for 'exclusion' / 'ip_based' rows these stay null while the 4.2
  // columns above carry the data
  non_cbs_characteristics:     NonCbsAssessmentRow[] | null
  interface_characteristics:   string | null
  functional_characteristics:  string | null
  conclusion_text:             string | null
}

// Phase A follow-up: single checklist item inside non_cbs_characteristics.
// SHI's 4.3 sections always carry 5 items: Data Processing, Programmable
// Logic, Software / OS, Digital Communication, Autonomous Control
export interface NonCbsAssessmentRow {
  item:        string
  assessment:  string
  explanation: string | null
}

export type ParseAssessmentResult = ParseAssessmentResultNew | ParseAssessmentResultLegacy

// vendor-form auto-route: drop a vendor-response form on the project
// overview → detect + extract identity (label-driven) + match against the
// project's systems. is_vendor_form=false means it's not the Q&A form and
// the normal import path should handle it
export interface VendorFormMatchCandidate {
  project_system_id: string
  label:             string
  vendor:            string | null
  function_name:     string | null
  score:             number
  confidence:        'auto' | 'review' | 'none'
}
export interface VendorFormMatchResult {
  is_vendor_form:   boolean
  detected_format?: string | null
  parsed?:          ParsedAssessment
  proposed_profile?: ProposedProfile
  identity?:        { yard_hull: string | null; system_name: string | null; vendor_name: string | null; model_name: string | null }
  source_filename?: string
  candidates?:      VendorFormMatchCandidate[]
  project_systems?: Array<{ id: string; label: string; vendor: string | null }>
}

export interface LegacyRaAssessment extends LegacyRaHeader, LegacyRaRow {
  id: string
  project_system_id: string
  source_filename: string | null
  imported_by: string | null
  imported_at: string
  updated_at: string
}

export interface CbsAssessmentRow {
  id: string
  system_profile_id: string
  q1_answer: string | null
  q2_answer: string | null
  q3_answer: string | null
  q4_answer: string | null
  q5_answer: string | null
  q6_answer: string | null
  q1_evidence_note: string | null
  q2_evidence_note: string | null
  q3_evidence_note: string | null
  q4_evidence_note: string | null
  q5_evidence_note: string | null
  q6_evidence_note: string | null
  is_cbs: boolean
  source_filename: string | null
  imported_at: string
}

export interface VendorResponseRow extends VendorResponse {
  id: string
  system_profile_id: string
  imported_at: string
}

export interface EvidenceFile {
  id: string
  // either system_profile_id or project_system_id is non-null per the
  // evidence_files_one_owner CHECK constraint (migration 029).
  // profile-bound files (cbs_question / vendor_response uploads from
  // the legacy EvidenceUploader path) keep their system_profile_id.
  // Phase 3.6 system-level uploads write project_system_id
  system_profile_id: string | null
  project_system_id: string | null
  attached_to_type: 'cbs_question' | 'vendor_response' | 'general'
  attached_to_key: string | null
  original_filename: string
  stored_filename: string
  mime_type: string | null
  file_size_bytes: number
  description: string | null
  uploaded_at: string
  // joined from auth.users by the list endpoint so the FE can render
  // "Uploaded by samuelthae@gmail.com" without a second round-trip.
  // only present on rows from listSystemEvidence, the upload
  // response (POST returns the bare insert) leaves it undefined
  uploader_email?: string
}

export interface AssessmentResponse {
  project_system: {
    id: string
    system_profile_id: string | null
    vendor_name_raw: string | null
    system_name: string | null
    system_description: string | null
    function_name: string | null
    vendor_name: string | null
    model_name: string | null
    profile_system_name: string | null
  }
  profile: {
    id: string
    vendor_name: string
    model_name: string
    system_name: string
  } | null
  cbs: CbsAssessmentRow | null
  vendor_responses: VendorResponseRow[]
  evidence: EvidenceFile[]
  linked_project_systems: Array<{
    id: string
    project_id: string
    hull_number: string
    shipyard_name: string | null
  }>
}

export interface ProjectContact {
  id:                   string
  full_name:            string | null
  email:                string | null
  phone:                string | null
  company_name:         string | null
  linked_systems_count: number
  linked_system_names:  string[]
  /** the shipyard's own owner(s) for this contact — SHI forms only */
  shipyard_contacts?:   string[]
  shipyard_contact_emails?: string[]
  created_at:           string
}

export interface AssetInventoryRow {
  id?:                              string
  item_number:                      number | null
  ship_functions_and_systems:       string | null
  system:                           string | null
  equipment:                        string | null
  system_category:                  string | null
  brand_manufacturer:               string | null
  model_type:                       string | null
  unique_identifier:                string | null
  os_version_patch:                 string | null
  firmware_version_patch:           string | null
  software_version:                 string | null
  location:                         string | null
  security_zone:                    string | null
  description_purpose:              string | null
  connections_within_scope:         string | null
  connections_untrusted_networks:   string | null
  physical_interfaces:              string | null
  communication_protocols:          string | null
  ip_ranges_mac_addresses:          string | null
  certificate_type_approval:        string | null
  excluded:                         boolean
  update_log:                       string | null
  matched_project_system_id?:       string | null
  project_system_id?:               string | null
  project_id?:                      string | null
  // #6 provenance: 'word_ra_inventory' rows are auto-written by a Word RA
  // mapping (Q4) and are read-only in the asset UI. NULL = manual/imported,
  // which is deletable
  source?:                          string | null
  linked_system_name?:              string | null
  linked_vendor?:                   string | null
  // Item A: scope classification verdict (in/out/uncertain) + provenance.
  // distinct from the workbook `excluded` flag and the imported Function
  // text. scope_source 'human' is set when the user edits in the preview
  scope_status?:                    'in_scope' | 'out_of_scope' | 'uncertain' | null
  scope_function?:                  string | null
  scope_source?:                    'matched' | 'learned' | 'ai' | 'human' | 'unclassified' | null
  scope_confidence?:                number | null
  scope_reasoning?:                 string | null
}

export interface ScopeSummary {
  in_scope:      number
  out_of_scope:  number
  uncertain:     number
  by_source:     Record<string, number>
  class_society: string
  ai_calls:      number
  est_cost_usd:  number
}

export interface ParseAssetInventoryResult {
  header: {
    vessel_name:      string | null
    imo_number:       string | null
    class_society_id: string | null
    shipyard_name:    string | null
    date:             string | null
  }
  rows:  AssetInventoryRow[]
  stats: { total: number; matched: number; unmatched: number }
  telemetry?: {
    headerRow:          number
    exampleRowsSkipped: number
    emptyRowsSkipped:   number
    rowsImported:       number
    filledDownCells?:   number
    stoppedAtRow?:      number | null
  }
  project_systems: Array<{
    id:                 string
    system_name:        string | null
    system_description: string | null
    vendor_name_raw:    string | null
  }>
  // Item A: the society's in-scope function display names (for the
  // preview's scope dropdown) + a roll-up of the classification pass
  scope_functions?: string[]
  scope_summary?:   ScopeSummary | null
}

export type SecurityZoneCode =
  | 'machinery_cargo'
  | 'navigation_communication'
  | 'safety'
  | 'data_exchange_monitoring'

export interface TopologyNode {
  id: string
  project_id: string
  project_system_id: string | null
  ra_system_assessment_id: string | null
  node_type: 'target' | 'interface' | 'boundary' | 'untrusted'
  zone_code: SecurityZoneCode | null
  system_name: string
  supplier: string | null
  ship_function: string | null
  location: string | null
  is_target: boolean
  assignment_source: 'rule' | 'inventory' | 'wiring' | 'human'
  assignment_confidence: number | string | null
  assignment_reason: string | null
  confirmed: boolean
  evidence: unknown[]
}

export interface CommunicationLink {
  id: string
  project_id: string
  from_node_id: string
  to_node_id: string
  conduit_id: string | null
  direction: 'unidirectional' | 'bidirectional' | 'unknown'
  physical_standard: 'ethernet' | 'rs232' | 'rs422' | 'rs485' | 'hardwired' | 'wireless' | 'unknown'
  protocol: string | null
  ports_services: string | null
  purpose: string | null
  source_text: string | null
  evidence: unknown[]
  confidence: number | string | null
  confirmed: boolean
}

export interface ProjectConduit {
  id: string
  project_id: string
  label: string
  from_zone: SecurityZoneCode
  to_zone: SecurityZoneCode | null
  to_untrusted: boolean
  security_profile: string
  purpose: string | null
  boundary_device: string | null
  boundary_control: string | null
  permitted_traffic: string | null
  evidence: unknown[]
  confirmed: boolean
}

export interface CsddControl {
  id: string
  project_id: string
  topology_node_id: string
  requirement_code: string
  status: 'complete' | 'not_applicable' | 'tbu'
  narrative: string
  reference: string | null
  evidence: unknown[]
  confirmed: boolean
}

export interface ComplianceTopology {
  nodes: TopologyNode[]
  links: CommunicationLink[]
  conduits: ProjectConduit[]
  controls: CsddControl[]
  issues: Array<{ kind: string; id?: string; message: string }>
}

export interface TestProcedureSource {
  key: string
  title: string
  class_society: string
  rule_profile: string
  allowed_profile_codes: string[]
  source_revision: string
  source_approved_at: string
  source_sha256: string
  system_count: number
}

export interface TestProcedureIntegrationBoundary {
  zone: string
  device: string
  model?: string | null
  address?: string | null
  subnet?: string | null
  connectedSystems?: string[]
}

export interface TestProcedureIntegrationConfig {
  location?: string
  monitoringSystem?: string
  monitoringModel?: string
  monitoringAddress?: string
  managementNetwork?: string
  gateway?: string
  mainBoundaryDevice?: string
  mainBoundaryModel?: string
  mainBoundaryAddress?: string
  testSource?: string
  floodRateMbps?: number
  boundaries?: TestProcedureIntegrationBoundary[]
}

export interface TestProcedureSourceSystem {
  key: string
  system: string
  supplier: string
  model: string
  pda: string | null
  source_pages: { start: number; end: number }
  topology_anchors: string[]
  review_flags: string[]
}

export interface TestProcedureSourceReview {
  project_identity_confirmed?: boolean
  rule_edition_confirmed?: boolean
  approved_documents_confirmed?: boolean
  topology_reconciled?: boolean
  rule_edition_basis?: string
  csdd_revision?: string
  drawing_revision?: string
  review_notes?: string
}

export interface TestProcedureSystemApplicabilityReview {
  installed_configuration_confirmed?: boolean
  capability_applicability_confirmed?: boolean
  network_path_confirmed?: boolean
  pda_scope_confirmed?: boolean
  integration_dependencies_resolved?: boolean
  test_safety_confirmed?: boolean
  known_conflicts_resolved?: boolean
}

export interface TestProcedureMapping {
  id: string
  ra_system_assessment_id: string
  source_system_key: string | null
  similarity_score: number | null
  match_basis: Record<string, unknown>
  review_status: 'suggested' | 'approved' | 'custom_required'
  overrides: { model?: string; pda?: string; location?: string }
  applicability_review: TestProcedureSystemApplicabilityReview
  review_notes: string | null
  reviewed_at: string | null
  resolution_kind: 'approved_exact' | 'approved_family' | 'generated_custom' | null
  resolution_provenance: Array<{
    source: string
    sourceId?: string | null
    label: string
    citation?: string | null
    confidence?: number | null
    currentProject: boolean
  }>
}

export interface TestProcedureTarget {
  id: string
  system_name: string
  supplier: string | null
  ship_function: string | null
  mapping: TestProcedureMapping | null
  candidates: Array<TestProcedureSourceSystem & {
    source_system_key: string
    score: number
    basis: Record<string, unknown>
  }>
}

export interface TestProcedureState {
  profile: ProjectComplianceProfile & {
    project_name: string | null
    hull_number: string
    vessel_name: string | null
    vessel_type: string | null
    ship_owner: string | null
    shipyard_name: string | null
    shipyard_code: string | null
  }
  source: null | {
    key: string
    title: string
    source_document_number: string
    source_revision: string
    source_approved_at: string
    source_sha256: string
    systems: TestProcedureSourceSystem[]
  }
  draft: null | {
    id: string
    source_key: string
    revision: string
    status: string
    integration_status: 'pending' | 'applicable' | 'not_applicable'
    integration_reason: string | null
    integration_config: TestProcedureIntegrationConfig
    source_review: TestProcedureSourceReview
    excluded_system_ids: string[]
    updated_at: string
  }
  targets: TestProcedureTarget[]
  selection_targets: Array<{
    id: string
    system_name: string
    supplier: string | null
    group: 'navigation' | 'other'
    included: boolean
    reusable: boolean
    resolution_kind: 'approved_exact' | 'approved_family' | 'generated_custom' | null
    resolution_label: string | null
    resolution_provenance: TestProcedureMapping['resolution_provenance']
  }>
  omitted_targets: Array<{
    id: string
    system_name: string
    reason: string
  }>
  generation_issues: string[]
  can_generate: boolean
}

export interface TestProcedureReadiness {
  ready: boolean
  run_status: string
  stale: boolean
  tbu_count: number
  conflict_count: number
  invalid_citation_count: number
  unreviewed_claim_count: number
  unreviewed_decision_count: number
  reasons: string[]
}

export interface TestProcedureResearchRun {
  id: string
  draft_id: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  model: string
  prompt_version: string
  selected_target_ids: string[]
  evidence_manifest_hash: string | null
  max_cost_usd: number | string | null
  cost_usd: number | string
  prompt_tokens: number
  completion_tokens: number
  total_systems: number
  completed_systems: number
  current_system: string | null
  error: string | null
  supported_count?: number
  conflict_count?: number
  tbu_count?: number
  pending_review_count?: number
  created_at: string
  finished_at: string | null
  readiness?: TestProcedureReadiness
}

export interface TestProcedureEvidence {
  id: string
  target_key: string
  evidence_kind: string
  evidence_scope: 'control' | 'context'
  label: string
  page_number: number | null
  content: string
  content_sha256: string
  metadata: Record<string, unknown>
}

export interface TestProcedureCitation {
  id: string
  evidence_id: string
  quote: string
  page_number: number | null
  validated: boolean
  validation_error: string | null
  evidence_label: string
}

export interface TestProcedureClaim {
  id: string
  run_id: string
  target_key: string
  criterion_code: string
  claim_type: string
  ordering_index: number
  text: string
  support_status: 'supported' | 'conflicting' | 'tbu'
  review_status: 'pending' | 'accepted' | 'rejected'
  citations: TestProcedureCitation[]
}

export interface TestProcedureCriterionDecision {
  id: string
  run_id: string
  target_key: string
  criterion_code: string
  applicability: 'applicable' | 'not_applicable' | 'omitted_by_approval' | 'tbu'
  basis: string
  approval_reference: string | null
  review_status: 'pending' | 'accepted' | 'rejected'
}

export interface TestProcedureResearchReview {
  claims: TestProcedureClaim[]
  decisions: TestProcedureCriterionDecision[]
}

export interface TestProcedureOutput {
  id: string
  research_run_id: string | null
  output_filename: string
  output_sha256: string
  output_bytes: number | null
  warning_count: number
  unsupported_count: number
  conflict_count: number
  unreviewed_count: number
  verification_status: 'legacy_unverified' | 'researched_draft' | 'class_ready' | 'stale'
  status: string
  generated_at: string
}

export const projectsApi = {
  // includeInactive is the admin management view; every other caller wants
  // the pickable list
  listShipyards:    (opts?: { includeInactive?: boolean }) =>
    apiFetch<Shipyard[]>('GET', `/api/projects/meta/shipyards${opts?.includeInactive ? '?all=1' : ''}`),
  createShipyard:   (body: { name: string; code?: string }) =>
    apiFetch<Shipyard>('POST', '/api/projects/meta/shipyards', body),
  updateShipyard:   (id: number, body: { name?: string; code?: string; is_active?: boolean }) =>
    apiFetch<Shipyard>('PUT', `/api/projects/meta/shipyards/${id}`, body),
  deleteShipyardLogo: (id: number) =>
    apiFetch<Shipyard>('DELETE', `/api/projects/meta/shipyards/${id}/logo`),

  uploadShipyardLogo: async (id: number, file: File): Promise<Shipyard> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/projects/meta/shipyards/${id}/logo`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Logo upload failed')
    }
    return await res.json()
  },

  listRegulations:  () => apiFetch<Regulation[]>('GET', '/api/projects/meta/regulations'),
  listComplianceProfiles: () =>
    apiFetch<ComplianceRuleProfile[]>('GET', '/api/projects/meta/compliance-profiles'),

  // returns [] on 404 so the UI still loads if route isn't deployed yet
  listProjects: async (): Promise<Project[]> => {
    const { data, error } = await apiRequest<Project[]>('GET', '/api/projects')
    if (error) {
      if (error.message.includes('404')) {
        console.warn(
          '[projectsApi] GET /api/projects returned 404. Redeploy the Express API with the projects routes, or confirm the reverse proxy forwards /api/projects.'
        )
        return []
      }
      throw new Error(error.message)
    }
    return (data ?? []) as Project[]
  },
  getProject:     (id: string) => apiFetch<ProjectDetail>('GET', `/api/projects/${id}`),
  createProject:  (body: CreateProjectInput) =>
    apiFetch<Project>('POST', '/api/projects', body),
  updateProject:  (id: string, body: UpdateProjectInput) =>
    apiFetch<Project>('PUT', `/api/projects/${id}`, body),
  deleteProject:  (id: string) =>
    apiFetch<{ deleted: boolean; id: string }>('DELETE', `/api/projects/${id}`),

  listNmsConfigurations: (projectId?: string, includeHistory = false) => {
    const params = new URLSearchParams()
    if (projectId) params.set('project_id', projectId)
    if (includeHistory) params.set('include_history', '1')
    const query = params.toString()
    return apiFetch<NmsConfiguration[]>('GET', `/api/nms-configurations${query ? `?${query}` : ''}`)
  },
  listNmsHistory: (id: string) =>
    apiFetch<NmsConfiguration[]>('GET', `/api/nms-configurations/${id}/history`),
  uploadNmsConfiguration: async (
    projectId: string,
    file: File,
    relativePath?: string,
  ): Promise<NmsUploadResult> => {
    const formData = new FormData()
    formData.append('file', file)
    if (relativePath) formData.append('relative_path', relativePath)
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/projects/${projectId}/nms-configurations`,
      { method: 'POST', body: formData },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(body.error ?? 'NMS configuration upload failed')
    }
    return res.json()
  },
  fetchNmsContent: async (id: string): Promise<Blob> => {
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/nms-configurations/${id}/content`)
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(body.error ?? 'Failed to load NMS configuration')
    }
    return res.blob()
  },
  downloadNmsConfiguration: async (row: Pick<NmsConfiguration, 'id' | 'original_filename'>): Promise<void> => {
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/nms-configurations/${row.id}/content?download=1`,
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(body.error ?? 'NMS configuration download failed')
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = row.original_filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  },
  deleteNmsConfiguration: (id: string) =>
    apiFetch<void>('DELETE', `/api/nms-configurations/${id}`),

  listSystems:         () => apiFetch<SystemListItem[]>('GET', '/api/systems'),
  listProjectSystems:  (id: string) => apiFetch<ProjectSystem[]>('GET', `/api/projects/${id}/systems`),

  // QA item 2 (2026-06-12): direct edit of a project_system's
  // descriptive fields from the project systems list. bypasses
  // apiFetch so the 409 NAME_TAKEN code survives for the modal's
  // inline field error, same pattern as splitProjectSystem
  updateProjectSystem: async (
    id: string,
    fields: Partial<Pick<ProjectSystem,
      'system_name' | 'vendor_name_raw' | 'function_name' |
      'system_description' | 'type_approval_status' | 'cbs_grade'>>,
  ): Promise<ProjectSystem> => {
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/project-systems/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const err: SplitProjectSystemError = Object.assign(
        new Error(body.error ?? `update failed: ${res.status}`),
        { status: res.status, code: body.code },
      )
      throw err
    }
    return res.json()
  },
  listProjectContacts: (id: string) => apiFetch<ProjectContact[]>('GET', `/api/projects/${id}/contacts`),

  listContacts:  () => apiFetch<GlobalContact[]>('GET', '/api/contacts'),
  deleteContact: (id: string) =>
    apiFetch<{ deleted: boolean }>('DELETE', `/api/contacts/${id}`),

  stageBatch:     (payload: StagePayload) =>
    apiFetch<{ batch_id: string; row_count: number }>('POST', '/api/imports/stage', payload),
  getBatch:       (batchId: string) =>
    apiFetch<ImportBatchDetail>('GET', `/api/imports/batches/${batchId}`),
  // commit = UPSERT (match → update in place, else insert; never deletes).
  // the legacy replace-wipe stays server-side behind super-admin for
  // emergency use only — deliberately not reachable from the UI.
  commitBatch:    (batchId: string) =>
    apiFetch<CommitResult>('POST', `/api/imports/batches/${batchId}/commit`, {}),
  deleteBatch:    (batchId: string) =>
    apiFetch<{ deleted: boolean }>('DELETE', `/api/imports/batches/${batchId}`),

  parseAssessment: async (projectSystemId: string, file: File): Promise<ParseAssessmentResult> => {
    // #3: large workbooks go through the chunked path (init/chunk/
    // complete) so each request stays well inside Cloudflare Free's ~100 s
    // edge timeout. small ones keep the simple single-shot POST. threshold
    // matches the soft-warning the FE already shows at ~8 MB
    if (file.size > ASSESSMENT_CHUNK_THRESHOLD_BYTES) {
      console.info(`[parse-assessment] ${file.name} ${(file.size / 1024 / 1024).toFixed(2)} MiB > 8 MiB — uploading in chunks`)
      return chunkedUpload<ParseAssessmentResult>({
        file,
        initUrl:     `/api/project-systems/${projectSystemId}/parse-assessment/upload-init`,
        chunkUrl:    `/api/project-systems/${projectSystemId}/parse-assessment/upload-chunk`,
        completeUrl: `/api/project-systems/${projectSystemId}/parse-assessment/upload-complete`,
      })
    }
    // diagnostic: pair with the server-side "[parse-assessment]
    // multer received ... in Nms" log line so we can correlate
    // browser-side upload start with multer's hand-off and the
    // res.on('close') timing on failure. CF Free's edge timeout is
    // ~100s for streamed uploads. if the size+upload-bandwidth math
    // suggests we're cutting it close, the next failure log makes
    // that obvious
    console.info(`[parse-assessment] uploading ${file.name} — ${file.size} bytes (${(file.size / 1024 / 1024).toFixed(2)} MiB)`)
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/project-systems/${projectSystemId}/parse-assessment`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Parse failed')
    }
    return await res.json()
  },

  commitAssessment: (projectSystemId: string, body: {
    use_existing_profile_id?: string
    // when declining reuse of a same-key profile: mint a '#N' variant key so
    // a genuinely separate profile row is created (plain upsert would return
    // and overwrite the existing row)
    create_variant?: boolean
    parsed: ParsedAssessment
    proposed_profile: ProposedProfile
    source_filename: string
    vai_rows?:          AssetInventoryRow[]
    vai_vessel_header?: VaiPreview['vessel_header']
  }) => apiFetch<{
    success:           boolean
    system_profile_id: string
    is_cbs:            boolean
    vai_deleted?:      number
    vai_inserted?:     number
    vai_skipped?:      number
  }>(
    'POST', `/api/project-systems/${projectSystemId}/commit-assessment`, body
  ),

  // vendor-form auto-route: parse a vendor-response form dropped on the
  // project overview and match it to a system. single-shot (vendor forms
  // are small). returns is_vendor_form=false if it's not the Q&A form
  vendorFormParseAndMatch: async (projectId: string, file: File): Promise<VendorFormMatchResult> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/projects/${projectId}/vendor-form/parse-and-match`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Vendor-form parse failed')
    }
    return await res.json()
  },

  getAssessment: (projectSystemId: string) =>
    apiFetch<AssessmentResponse>('GET', `/api/project-systems/${projectSystemId}/assessment`),

  uploadEvidence: async (profileId: string, file: File, body: {
    attached_to_type: 'cbs_question' | 'vendor_response' | 'general'
    attached_to_key?: string
    description?: string
  }): Promise<EvidenceFile> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('attached_to_type', body.attached_to_type)
    if (body.attached_to_key) formData.append('attached_to_key', body.attached_to_key)
    if (body.description) formData.append('description', body.description)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/system-profiles/${profileId}/evidence`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Upload failed')
    }
    return await res.json()
  },

  // Phase 3.6 system-level evidence endpoints. hit a different URL
  // than uploadEvidence above: this one binds the file to the
  // project_system directly (no profile required), and the listing
  // returns BOTH profile-bound and direct-bound rows so the FE
  // doesn't have to know about the dual ownership shape
  listSystemEvidence: (projectSystemId: string) =>
    apiFetch<EvidenceFile[]>('GET', `/api/project-systems/${projectSystemId}/evidence`),

  uploadSystemEvidence: async (
    projectSystemId: string,
    file: File,
    description?: string,
  ): Promise<EvidenceFile> => {
    const formData = new FormData()
    formData.append('file', file)
    if (description) formData.append('description', description)
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/project-systems/${projectSystemId}/evidence`,
      { method: 'POST', body: formData },
    )
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Upload failed')
    }
    return await res.json()
  },

  deleteEvidence: (id: string) =>
    apiFetch<{ deleted: boolean }>('DELETE', `/api/evidence-files/${id}`),

  /**
   * Request a short-lived signed URL for downloading an evidence file.
   * Use this and window.location.href / <a onClick>, browser navigation
   * to the returned URL Just Works (HMAC in ?token=…, no Authorization
   * header required).
   */
  signEvidenceDownload: async (id: string): Promise<string> => {
    const res = await apiFetch<{ token: string; url: string; expires_at: number }>(
      'POST', `/api/evidence-files/${id}/sign`, {}
    )
    return `${API_BASE_URL}${res.url}`
  },

  // crudRouter list, most recent releases first. default 10, the
  // dashboard card only needs the latest of each edition (Full + Lite),
  // and we filter client-side by filename rather than adding an
  // edition column to the schema
  listRecentReleases: (limit = 10) =>
    apiFetch<ReleaseRow[]>('GET', `/api/releases?orderBy=created_at:desc&limit=${limit}`),

  // server-computed per-release download availability: R2-migrated or all
  // chunk files present on disk. releases orphaned by the April migration
  // (bytes lost, rows kept) come back available:false so the FE can disable
  // their Download buttons instead of failing at click time
  getReleaseAvailability: () =>
    apiFetch<{ id: number; available: boolean }[]>('GET', '/api/releases/availability'),

  /**
   * @deprecated The bare /download route requires JWT auth in its header,
   * which <a href> navigation cannot attach. Use signEvidenceDownload(id)
   * instead and set window.location.href to the returned URL. Scheduled
   * for removal once all callers are migrated.
   */
  evidenceDownloadUrl: (id: string) => {
    if (typeof console !== 'undefined') {
      console.warn('[projectsApi] evidenceDownloadUrl is deprecated — use signEvidenceDownload() and window.location.href')
    }
    return `${API_BASE_URL}/api/evidence-files/${id}/download`
  },

  updateCbs: (id: string, body: Partial<{
    q1_answer: 'Y' | 'N' | null
    q2_answer: 'Y' | 'N' | null
    q3_answer: 'Y' | 'N' | null
    q4_answer: 'Y' | 'N' | null
    q5_answer: 'Y' | 'N' | null
    q6_answer: 'Y' | 'N' | null
    q1_evidence_note: string | null
    q2_evidence_note: string | null
    q3_evidence_note: string | null
    q4_evidence_note: string | null
    q5_evidence_note: string | null
    q6_evidence_note: string | null
  }>) => apiFetch<any>('PATCH', `/api/cbs-assessments/${id}`, body),

  updateVendorResponse: (id: string, body: Partial<{
    dropdown_response: string | null
    detailed_response: string | null
    remark: string | null
  }>) => apiFetch<any>('PATCH', `/api/vendor-responses/${id}`, body),

  updateEvidence: (id: string, body: Partial<{
    description: string
    attached_to_type: string
    attached_to_key: string
  }>) => apiFetch<any>('PATCH', `/api/evidence-files/${id}`, body),

  updateProfile: (id: string, body: Partial<{
    vendor_name: string
    model_name: string
    system_name: string
    notes: string
  }>) => apiFetch<any>('PATCH', `/api/system-profiles/${id}`, body),

  parseAssetInventory: async (projectId: string, file: File): Promise<ParseAssetInventoryResult> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/projects/${projectId}/asset-inventory/parse`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Parse failed')
    }
    return await res.json()
  },

  commitAssetInventory: (projectId: string, body: {
    rows:               AssetInventoryRow[]
    source_filename:    string
    replace_existing?:  boolean
  }) => apiFetch<{ success: boolean; inserted: number; learned?: number }>(
    'POST', `/api/projects/${projectId}/asset-inventory/commit`, body
  ),

  getAssetInventory: (projectId: string) =>
    apiFetch<AssetInventoryRow[]>('GET', `/api/projects/${projectId}/asset-inventory`),

  // #6: delete a single MANUAL inventory row (admin). 409s on a
  // word_ra_inventory row (managed by the RA mapping)
  deleteAssetInventoryRow: (id: string) =>
    apiFetch<{ deleted: boolean }>('DELETE', `/api/asset-inventory/${id}`),

  // #6: clear all MANUAL inventory rows for a project (admin)
  clearManualAssetInventory: (projectId: string) =>
    apiFetch<{ deleted: number }>('DELETE', `/api/projects/${projectId}/asset-inventory/manual`),

  getSystemInventory: (systemId: string) =>
    apiFetch<AssetInventoryRow[]>('GET', `/api/project-systems/${systemId}/asset-inventory`),

  // system-scoped VAI upload: every row gets project_system_id = systemId,
  // no vendor+equipment auto-matching. project-level parse/commit still does
  // the matching for bulk imports
  parseSystemAssetInventory: async (systemId: string, file: File): Promise<{
    project_system_id: string
    project_id:        string
    vessel_header:     VaiPreview['vessel_header']
    rows:              AssetInventoryRow[]
    row_count:         number
    telemetry?:        unknown
  }> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/project-systems/${systemId}/asset-inventory/parse`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error ?? 'Parse failed')
    }
    return await res.json()
  },

  // REPLACE semantics: the commit deletes the system's existing Excel/manual
  // inventory rows and inserts these (Word-RA-sourced rows untouched)
  commitSystemAssetInventory: (systemId: string, body: {
    vai_rows:         AssetInventoryRow[]
    vessel_header:    VaiPreview['vessel_header']
    source_filename?: string
  }) => apiFetch<{ success: boolean; vai_deleted: number; vai_inserted: number; vai_skipped: number }>(
    'POST', `/api/project-systems/${systemId}/asset-inventory/commit`, body
  ),

  // clears ONE system's Excel-uploaded + manual inventory rows (the undo for
  // a wrong per-system VAI upload). Word-RA pipeline rows are not affected
  clearSystemAssetInventory: (systemId: string) =>
    apiFetch<{ deleted: number }>('DELETE', `/api/project-systems/${systemId}/asset-inventory`),

  // ── Word RA upload (Phase A) ─────────────────────────────────────────────
  // chunked upload: uses the /upload-init, /upload-chunk, /upload-complete
  // endpoints to dodge Cloudflare Free's 100 s edge timeout. the legacy
  // single-shot /parse endpoint stays mounted server-side as @deprecated,
  // callers should pass through here instead
  parseRaDocument: async (
    projectId: string,
    file:      File,
    onProgress?: (pct: number) => void,
  ): Promise<ParseRaDocumentResult> => {
    return chunkedUpload<ParseRaDocumentResult>({
      file,
      initUrl:     `/api/projects/${projectId}/ra-documents/upload-init`,
      chunkUrl:    `/api/projects/${projectId}/ra-documents/upload-chunk`,
      completeUrl: `/api/projects/${projectId}/ra-documents/upload-complete`,
      onProgress:  (loaded, total) => onProgress?.(total > 0 ? Math.round((loaded / total) * 100) : 0),
    })
  },

  commitRaDocument: (projectId: string, body: {
    parsed:           ParsedRa
    class_society:    string | null
    source_filename:  string
    session_id?:      string
    match_overrides?: Record<string, string | null>
  }) => apiFetch<{
    ra_document_id:         string
    master_row_count:       number
    detail_count:           number
    linked_profiles_count:  number
    unmatched_detail_count: number
    diagrams_count:         number
    diagrams_dedup_count:   number
    match_resolved_count:   number
    match_skip_count:       number
  }>('POST', `/api/projects/${projectId}/ra-documents/commit`, body),

  listRaDocuments: (projectId: string) =>
    apiFetch<RaDocumentRow[]>('GET', `/api/projects/${projectId}/ra-documents`),

  getRaDocument: (raDocumentId: string) =>
    apiFetch<{ document: RaDocumentRow; assessments: RaSystemAssessmentRow[]; details: RaExcludedDetailRow[] }>(
      'GET', `/api/ra-documents/${raDocumentId}`,
    ),

  // per-system Word RA (Phase B3). 404 when this project_system has no
  // ra_system_assessment linked, caller renders an empty state
  getSystemWordRa: (projectSystemId: string) =>
    apiFetch<SystemWordRaResponse>('GET', `/api/project-systems/${projectSystemId}/word-ra`),

  // sign a single diagram for inline <img src> rendering. returns a
  // ~5min HMAC-signed public URL, same pattern as signEvidenceDownload
  signRaDiagram: async (id: string): Promise<string> => {
    const res = await apiFetch<{ token: string; url: string; expires_at: number }>(
      'POST', `/api/ra-diagram-files/${id}/sign`, {}
    )
    return `${API_BASE_URL}${res.url}`
  },

  // single-row mapping update for an ra_system_assessment
  patchRaAssessmentMapping: (assessmentId: string, projectSystemId: string | null) =>
    apiFetch<RaSystemAssessmentRow>(
      'PATCH', `/api/ra-system-assessments/${assessmentId}/mapping`,
      { project_system_id: projectSystemId },
    ),

  // batch mapping update: all updates run in one transaction. caller
  // typically builds this from a dirty-row diff in the mappings panel
  patchRaDocumentMappings: (raDocumentId: string, updates: Array<{
    ra_system_assessment_id: string
    project_system_id:       string | null
  }>) =>
    apiFetch<{ updated_count: number }>(
      'PATCH', `/api/ra-documents/${raDocumentId}/mappings`,
      { updates },
    ),

  // ── AI drawing screening (Phase 5 review) ───────────────────────────────
  // all endpoints requireAdmin server-side; the tab is hidden from
  // non-admins client-side (visibility only — backend re-checks)

  listScreeningRuns: (projectId: string) =>
    apiFetch<ScreeningRunRow[]>('GET', `/api/projects/${projectId}/ra-screening/runs`),

  getScreeningRun: (runId: string) =>
    apiFetch<ScreeningRunRow & { drawings: ScreeningDrawingRow[] }>('GET', `/api/ra-screening/${runId}`),

  // retry ONE failed drawing inside its existing run. The server replaces
  // that drawing's rows rather than appending a second set, and the
  // drawings that already succeeded are skipped.
  rescreenDrawing: (drawingId: string) =>
    apiFetch<{ run_id: string; drawing_id: string; rows_replaced: number; status: string }>(
      'POST', `/api/ra-screening/drawings/${drawingId}/rescreen`),

  // every drawing in the run, from scratch — for a fix to the reader
  // itself rather than to one volume
  rescreenRun: (runId: string) =>
    apiFetch<{ run_id: string; drawings: number; rows_replaced: number; status: string }>(
      'POST', `/api/ra-screening/runs/${runId}/rescreen-all`),

  listScreeningSystems: (runId: string) =>
    apiFetch<ScreeningSystemRow[]>('GET', `/api/ra-screening/${runId}/systems`),

  patchScreeningSystem: (systemId: string, body:
    | { action: 'confirm' }
    | { action: 'reject'; reason?: string }
    | { action: 'edit'; fields: Partial<Pick<ScreeningSystemRow,
        'system_name' | 'model' | 'maker' | 'cbs' | 'ship_function' | 'detail' | 'ip' | 'connected_system' | 'remark'>> }) =>
    apiFetch<ScreeningSystemRow>('PATCH', `/api/ra-screening/systems/${systemId}`, body),

  // Export-only merge groups. These NEVER touch the screening rows —
  // the app's results are identical whether or not a pass has run.
  screeningMergeGroups: (runId: string) =>
    apiFetch<{
      merge_pass: { status: string; model?: string; at?: string; considered?: number; groups?: number; error?: string } | null
      groups: Array<{ id: string; member_ids: string[]; display_name: string; reason: string | null; model: string | null; edited_by: string | null }>
      members: Array<{ id: string; system_name: string | null; model: string | null; maker: string | null; ip: string | null }>
    }>('GET', `/api/ra-screening/${runId}/merge-groups`),

  analyseScreeningMerge: (runId: string) =>
    apiFetch<{ status: string; considered: number; groups: unknown[] }>(
      'POST', `/api/ra-screening/${runId}/merge-analyse`, {}),

  splitMergeGroup: (groupId: string) =>
    apiFetch<{ split: boolean }>('POST', `/api/ra-screening/merge-groups/${groupId}/split`, {}),

  excludeFromMergeGroup: (groupId: string, rowId: string) =>
    apiFetch<{ remaining: number }>('POST', `/api/ra-screening/merge-groups/${groupId}/exclude`, { row_id: rowId }),

  bulkConfirmScreening: (runId: string) =>
    apiFetch<{ confirmed: number; unresolved: number }>('POST', `/api/ra-screening/${runId}/bulk-confirm`, {}),

  // drawings attach to the project's live run by default — adding Power
  // after Navi/Control/Fire extends one result set instead of opening a
  // second. newRun forces a fresh run (a revised drawing set).
  createScreeningRun: (projectId: string, uploadSessionIds: string[], newRun = false) =>
    apiFetch<{ run_id: string; drawings: number; status: string; attached_to_existing?: boolean; skipped_duplicates?: string[] }>(
      'POST', `/api/projects/${projectId}/ra-screening`, { upload_session_ids: uploadSessionIds, new_run: newRun }),

  promoteScreening: (runId: string, dryRun: boolean) =>
    apiFetch<{
      dry_run: boolean; created: number; updated: number
      skipped_component: number; skipped_infrastructure: number
      skipped_ambiguous: number; skipped_already: number; not_confirmed: number
      to_create: string[]
      to_update: Array<{ screening: string; existing: string; existing_id: string }>
      ambiguous: Array<{ screening: string; candidates: string[] }>
    }>('POST', `/api/ra-screening/${runId}/promote`, { dry_run: dryRun }),

  // ── Auto-map (Phase E1) ─────────────────────────────────────────────────
  // two-phase confirm: first call returns the count + cost estimate
  // without spending anything, second call (confirm=true) starts the
  // background session
  estimateAutoMap: (raDocumentId: string) =>
    apiFetch<{
      unmatched_count:     number
      estimated_cost_usd:  number
      cost_cap_usd?:       number
      already_mapped?:     boolean
    }>('POST', `/api/ra-documents/${raDocumentId}/auto-map`, { confirm: false }),

  startAutoMap: (raDocumentId: string) =>
    apiFetch<{ session_id: string }>(
      'POST', `/api/ra-documents/${raDocumentId}/auto-map`, { confirm: true },
    ),

  getAutoMapStatus: (sessionId: string) =>
    apiFetch<AutoMapSessionStatus>('GET', `/api/ra-mapping-sessions/${sessionId}/status`),

  cancelAutoMap: (sessionId: string) =>
    apiFetch<{ state: AutoMapSessionStatus['state'] }>(
      'POST', `/api/ra-mapping-sessions/${sessionId}/cancel`, {},
    ),

  // ── AI suggestion lifecycle (Phase E1.5) ────────────────────────────────
  // fetch persisted suggestions for a doc, filtered by lifecycle state.
  // `state` accepts a single state or comma-separated multiple states
  getAiSuggestions: (raDocumentId: string, state: string = 'pending') =>
    apiFetch<{ suggestions: AiSuggestionRow[] }>(
      'GET', `/api/ra-documents/${raDocumentId}/ai-suggestions?state=${encodeURIComponent(state)}`,
    ),

  // custom-path (not via apiFetch) because the 409 body carries a
  // structured `code` + optional `conflicting_assessment` that the FE
  // needs to render the named-conflict toast. apiFetch's request()
  // wrapper collapses errors to a single string message and drops the
  // sibling fields. here we promote them onto the thrown Error so the
  // AI Suggestions pane can branch on err.code instead of parsing the
  // human message
  acceptAiSuggestion: async (suggestionId: string): Promise<{
    state: 'accepted'; assessment_id: string; project_system_id: string
  }> => {
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/ai-suggestions/${suggestionId}/accept`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const err: AcceptSuggestionError = Object.assign(
        new Error(body.error ?? `accept failed: ${res.status}`),
        {
          status:                 res.status,
          code:                   body.code as AcceptSuggestionErrorCode | undefined,
          conflicting_assessment: body.conflicting_assessment ?? null,
        },
      )
      throw err
    }
    return res.json()
  },

  // Word RA authority Phase 2 endpoints, Phase 3 UI wiring. both
  // bypass apiFetch for the same reason acceptAiSuggestion does: the
  // 409 body carries machine-readable fields (code, optionally
  // existing_project_system or other) that the apiFetch wrapper
  // collapses to a single string

  splitProjectSystem: async (
    projectSystemId: string,
    body:            { new_system_name: string; assessment_id: string },
  ): Promise<ProjectSystem> => {
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/project-systems/${projectSystemId}/split`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      },
    )
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}))
      const err: SplitProjectSystemError = Object.assign(
        new Error(respBody.error ?? `split failed: ${res.status}`),
        {
          status: res.status,
          code:   respBody.code as SplitProjectSystemError['code'] | undefined,
        },
      )
      throw err
    }
    return res.json()
  },

  // Phase 3.5 AI function suggestion. backend never 5xxes, failures
  // return 200 with { suggestion: null } so the modal can stay
  // optimistic. no typed-error class needed here, just a
  // catch-all that resolves null on transport errors so the modal
  // never has to await a rejection
  suggestFunctionForSystem: async (
    projectId:   string,
    body:        { system_name: string; supplier?: string | null },
  ): Promise<{ suggestion: string | null; source: 'ai' | null; candidate_count: number }> => {
    try {
      const res = await fetchWithAuthRetry(
        `${API_BASE_URL}/api/projects/${projectId}/suggest-function`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(body),
        },
      )
      if (!res.ok) return { suggestion: null, source: null, candidate_count: 0 }
      return res.json()
    } catch {
      // network or ApiDownError, fall back to no-suggestion. the
      // Create modal must never block on this call
      return { suggestion: null, source: null, candidate_count: 0 }
    }
  },

  createProjectSystemFromAssessment: async (
    assessmentId: string,
    body?:        { function_name?: string | null; function_source?: 'doc' | 'ai' | 'manual' },
  ): Promise<ProjectSystem> => {
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/ra-system-assessments/${assessmentId}/create-project-system`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body ?? {}),
      },
    )
    if (!res.ok) {
      const respBody = await res.json().catch(() => ({}))
      const err: CreateProjectSystemError = Object.assign(
        new Error(respBody.error ?? `create failed: ${res.status}`),
        {
          status:                   res.status,
          code:                     respBody.code as CreateProjectSystemError['code'] | undefined,
          existing_project_system:  respBody.existing_project_system ?? null,
        },
      )
      throw err
    }
    return res.json()
  },

  rejectAiSuggestion: (suggestionId: string) =>
    apiFetch<{ state: 'rejected'; assessment_id: string }>(
      'POST', `/api/ai-suggestions/${suggestionId}/reject`, {},
    ),

  dismissAiSuggestion: (suggestionId: string) =>
    apiFetch<{ state: 'dismissed'; assessment_id: string }>(
      'POST', `/api/ai-suggestions/${suggestionId}/dismiss`, {},
    ),

  clearAllAiSuggestions: (raDocumentId: string) =>
    apiFetch<{ cleared: number }>(
      'POST', `/api/ra-documents/${raDocumentId}/ai-suggestions/clear`, {},
    ),

  // re-parse a Word RA document against its persisted source .docx.
  // replaces ra_excluded_details + ra_diagram_files atomically, does
  // NOT touch ra_system_assessments (project_system_id mappings + AI
  // suggestions survive). 400 when no source file is stored for the
  // doc (e.g. uploaded before migration 026), UI surfaces the
  // server's "Delete and re-upload manually." message verbatim
  reparseRaDocument: (raDocumentId: string) =>
    apiFetch<{
      details_replaced:        number
      diagrams_replaced:       number
      unmatched_detail_count?: number
      parse_warnings?:         string[]
    }>('POST', `/api/ra-documents/${raDocumentId}/reparse`, {}),

  // ── RA-from-selected-systems flow ────────────────────────────────────────
  // browse every assessed system in the library (deduped by name + vendor)
  listRaLibrary: () =>
    apiFetch<RaLibrarySystem[]>('GET', '/api/ra-library'),

  // presence-gated deliverable availability for the Documents tab (read-only),
  // plus the project requirements checklist (same predicates as the dashboard)
  getDeliverables: (projectId: string) =>
    apiFetch<{ deliverables: ProjectDeliverable[]; requirements: ProjectRequirements }>(
      'GET', `/api/projects/${projectId}/deliverables`),

  getComplianceTopology: (projectId: string) =>
    apiFetch<ComplianceTopology>('GET', `/api/projects/${projectId}/compliance-topology`),

  listTestProcedureSources: () =>
    apiFetch<TestProcedureSource[]>('GET', '/api/test-procedure-sources'),

  getTestProcedureState: (projectId: string, profileCode?: string | null) =>
    apiFetch<TestProcedureState>(
      'GET',
      `/api/projects/${projectId}/test-procedure${profileCode ? `?profile_code=${encodeURIComponent(profileCode)}` : ''}`,
    ),

  createTestProcedureDraft: (projectId: string, sourceKey: string, profileCode?: string | null) =>
    apiFetch<TestProcedureState>('POST', `/api/projects/${projectId}/test-procedure/draft`, {
      source_key: sourceKey,
      profile_code: profileCode ?? undefined,
    }),

  autoPrepareTestProcedure: (
    projectId: string,
    profileCode?: string | null,
    includedAssessmentIds?: string[],
  ) =>
    apiFetch<TestProcedureState>('POST', `/api/projects/${projectId}/test-procedure/auto-prepare`, {
      profile_code: profileCode ?? undefined,
      included_assessment_ids: includedAssessmentIds,
    }),

  startTestProcedureResearch: (
    projectId: string,
    profileCode: string | null,
    targetIds: string[],
  ) => apiFetch<TestProcedureResearchRun>('POST', `/api/projects/${projectId}/test-procedure/research`, {
    profile_code: profileCode ?? undefined,
    target_ids: targetIds,
  }),

  listTestProcedureResearchRuns: (projectId: string) =>
    apiFetch<TestProcedureResearchRun[]>('GET', `/api/projects/${projectId}/test-procedure/research-runs`),

  getTestProcedureResearchRun: (projectId: string, runId: string) =>
    apiFetch<TestProcedureResearchRun>('GET', `/api/projects/${projectId}/test-procedure/research-runs/${runId}`),

  cancelTestProcedureResearch: (projectId: string, runId: string) =>
    apiFetch<{ cancelled: boolean }>('POST', `/api/projects/${projectId}/test-procedure/research-runs/${runId}/cancel`),

  resumeTestProcedureResearch: (projectId: string, runId: string) =>
    apiFetch<{ queued: boolean; run_id: string }>('POST', `/api/projects/${projectId}/test-procedure/research-runs/${runId}/resume`),

  listTestProcedureEvidence: (projectId: string, runId: string, targetKey?: string | null) =>
    apiFetch<TestProcedureEvidence[]>(
      'GET',
      `/api/projects/${projectId}/test-procedure/research-runs/${runId}/evidence${targetKey ? `?target_key=${encodeURIComponent(targetKey)}` : ''}`,
    ),

  listTestProcedureClaims: (projectId: string, runId: string, targetKey?: string | null) =>
    apiFetch<TestProcedureResearchReview>(
      'GET',
      `/api/projects/${projectId}/test-procedure/research-runs/${runId}/claims${targetKey ? `?target_key=${encodeURIComponent(targetKey)}` : ''}`,
    ),

  reviewTestProcedureClaim: (
    projectId: string,
    claimId: string,
    reviewStatus: 'accepted' | 'rejected',
  ) => apiFetch<TestProcedureClaim>(
    'PATCH',
    `/api/projects/${projectId}/test-procedure/claims/${claimId}`,
    { review_status: reviewStatus },
  ),

  reviewTestProcedureCriterion: (
    projectId: string,
    decisionId: string,
    body: {
      applicability: TestProcedureCriterionDecision['applicability']
      basis: string
      approval_reference?: string | null
      review_status: 'accepted' | 'rejected'
    },
  ) => apiFetch<TestProcedureCriterionDecision>(
    'PATCH',
    `/api/projects/${projectId}/test-procedure/criterion-decisions/${decisionId}`,
    body,
  ),

  acceptSupportedTestProcedureClaims: (projectId: string, runId: string) =>
    apiFetch<{ accepted_claims: number; accepted_decisions: number }>(
      'POST',
      `/api/projects/${projectId}/test-procedure/research-runs/${runId}/accept-supported`,
    ),

  generateTestProcedureOutput: (
    projectId: string,
    runId: string,
    mode: 'draft' | 'class-ready',
  ) => apiFetch<TestProcedureOutput>(
    'POST',
    `/api/projects/${projectId}/test-procedure/research-runs/${runId}/outputs/${mode}`,
  ),

  listTestProcedureOutputs: (projectId: string) =>
    apiFetch<TestProcedureOutput[]>('GET', `/api/projects/${projectId}/test-procedure/outputs`),

  downloadStoredTestProcedureOutput: async (
    projectId: string,
    outputId: string,
    filename: string,
  ): Promise<void> => {
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/projects/${projectId}/test-procedure/outputs/${outputId}/download`,
      { method: 'GET' },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error ?? `Failed to download Test Procedure output (HTTP ${res.status})`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },

  updateTestProcedureSourceReview: (
    projectId: string,
    profileCode: string,
    sourceReview: TestProcedureSourceReview,
  ) => apiFetch<TestProcedureState>(
    'PATCH',
    `/api/projects/${projectId}/test-procedure/source-review`,
    { profile_code: profileCode, source_review: sourceReview },
  ),

  updateTestProcedureIntegration: (
    projectId: string,
    body: {
      profile_code?: string | null
      integration_status: 'pending' | 'applicable' | 'not_applicable'
      integration_reason?: string | null
      integration_config?: TestProcedureIntegrationConfig
    },
  ) => apiFetch<TestProcedureState>('PATCH', `/api/projects/${projectId}/test-procedure/integration`, body),

  reviewTestProcedureSystem: (
    projectId: string,
    assessmentId: string,
    body: {
      profile_code?: string | null
      source_system_key: string
      approved: boolean
      overrides?: { model?: string; pda?: string; location?: string }
      applicability_review?: TestProcedureSystemApplicabilityReview
      review_notes?: string | null
    },
  ) => apiFetch<TestProcedureState>(
    'PATCH',
    `/api/projects/${projectId}/test-procedure/systems/${assessmentId}`,
    body,
  ),

  downloadTestProcedure: async (
    projectId: string,
    filename: string,
    profileCode?: string | null,
  ): Promise<void> => {
    const query = profileCode ? `?profile_code=${encodeURIComponent(profileCode)}` : ''
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/projects/${projectId}/test-procedure/generate${query}`,
      { method: 'GET' },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const details = Array.isArray(body.details) ? ` ${body.details.join(' ')}` : ''
      throw new Error(`${body.error ?? `Failed to generate Test Procedure (HTTP ${res.status})`}${details}`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    a.remove(); URL.revokeObjectURL(url)
  },

  buildComplianceTopology: (projectId: string, force = false) =>
    apiFetch<ComplianceTopology>('POST', `/api/projects/${projectId}/compliance-topology/draft`, { force }),

  updateTopologyNode: (projectId: string, nodeId: string, patch: Partial<TopologyNode>) =>
    apiFetch<ComplianceTopology>('PATCH', `/api/projects/${projectId}/compliance-topology/nodes/${nodeId}`, patch),

  createCommunicationLink: (
    projectId: string,
    input: Pick<CommunicationLink, 'from_node_id' | 'to_node_id'>
      & Partial<Pick<CommunicationLink, 'direction' | 'physical_standard' | 'protocol' | 'ports_services' | 'purpose' | 'source_text' | 'confirmed'>>,
  ) => apiFetch<ComplianceTopology>('POST', `/api/projects/${projectId}/compliance-topology/links`, input),

  updateCommunicationLink: (projectId: string, linkId: string, patch: Partial<CommunicationLink>) =>
    apiFetch<ComplianceTopology>('PATCH', `/api/projects/${projectId}/compliance-topology/links/${linkId}`, patch),

  updateProjectConduit: (projectId: string, conduitId: string, patch: Partial<ProjectConduit>) =>
    apiFetch<ComplianceTopology>('PATCH', `/api/projects/${projectId}/compliance-topology/conduits/${conduitId}`, patch),

  updateCsddControl: (projectId: string, controlId: string, patch: Partial<CsddControl>) =>
    apiFetch<ComplianceTopology>('PATCH', `/api/projects/${projectId}/csdd-controls/${controlId}`, patch),

  // Documents-only, persisted evidence-backed RA orchestration. GETs are
  // read-only resume/status calls; paid preparation starts only after the
  // caller supplies the explicit aggregate cap confirmation.
  getRaGeneration: (projectId: string, profileCode?: string | null) =>
    apiFetch<RaGenerationState>(
      'GET',
      `/api/projects/${projectId}/ra-generation${profileCode ? `?profile_code=${encodeURIComponent(profileCode)}` : ''}`,
    ),

  startRaGeneration: (projectId: string, profileCode: string, maxCostUsd: number) =>
    apiFetch<RaGenerationRun | { run: RaGenerationRun }>(
      'POST',
      `/api/projects/${projectId}/ra-generation/start`,
      { profile_code: profileCode, confirmed: true, max_cost_usd: maxCostUsd },
    ),

  getRaGenerationRun: (runId: string) =>
    apiFetch<RaGenerationRun | { run: RaGenerationRun }>('GET', `/api/ra-generation/${runId}`),

  retryRaGeneration: (runId: string) =>
    apiFetch<RaGenerationRun | { run: RaGenerationRun }>('POST', `/api/ra-generation/${runId}/retry`),

  finalizeRaGeneration: (runId: string) =>
    apiFetch<RaGenerationRun | { run: RaGenerationRun }>('POST', `/api/ra-generation/${runId}/finalize`),

  applyRaInstruction: (projectId: string, instruction: string) =>
    apiFetch<RaInstructionResult>('POST', `/api/projects/${projectId}/ra-instructions`, { instruction }),

  // start an empty RA on a project (society/names carried from the project)
  createBlankRa: (projectId: string, profileCode?: string) =>
    apiFetch<{ ra_document_id: string; compliance_profile_code: string }>(
      'POST',
      `/api/projects/${projectId}/ra-documents/blank`,
      profileCode ? { profile_code: profileCode } : {},
    ),

  // copy chosen library systems (assessment + deep-dive + diagrams) into the RA.
  // withAssetInventory also populates the project's Vessel Asset Inventory
  // (systems_190) from the same systems, copying a real record where one exists,
  // else deriving it from the RA detail
  addSystemsToRa: (raDocumentId: string, sourceAssessmentIds: string[], withAssetInventory = false) =>
    apiFetch<{ added: number; inventory_added?: number }>('POST', `/api/ra-documents/${raDocumentId}/add-systems`, {
      source_assessment_ids: sourceAssessmentIds,
      with_asset_inventory: withAssetInventory,
    }),

  // AI-map this vessel's own systems (project_systems) to the library, so the
  // picker can pre-select the matches. full-AI matching, on demand
  automapRaSystems: (projectId: string, profileCode?: string) =>
    apiFetch<{
      matches: Array<{
        project_system_id: string
        project_system_name: string
        vendor: string | null
        matched_source_assessment_id: string | null
        matched_name: string | null
        matched_supplier: string | null
        matched_is_complete: boolean | null
        confidence: 'high' | 'medium' | 'low' | null
      }>
      matched_count: number
      complete_count?: number
      incomplete_count?: number
      total: number
      note?: string
    }>('POST', `/api/projects/${projectId}/ra-automap`, profileCode ? { profile_code: profileCode } : {}),

  // the Asset Inventory DELIVERABLE: styled IACS Rec-190 template fill
  // (title block + all 22 columns, re-importable). /export below stays the
  // bare data grid.
  generateAssetInventoryDeliverable: async (projectId: string, filename: string, targetOnly = false): Promise<void> => {
    const suffix = targetOnly ? '?scope=target' : ''
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/projects/${projectId}/asset-inventory/generate${suffix}`, { method: 'GET' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error ?? `Failed to generate asset inventory (HTTP ${res.status})`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    a.remove(); URL.revokeObjectURL(url)
  },

  downloadComplianceDocument: async (
    projectId: string,
    type: 'zcd' | 'csdd',
    filename: string,
    revision = '0',
    profileCode?: string,
  ): Promise<void> => {
    const query = new URLSearchParams({ revision })
    if (profileCode) query.set('profile_code', profileCode)
    const res = await fetchWithAuthRetry(
      `${API_BASE_URL}/api/projects/${projectId}/compliance-documents/${type}?${query}`,
      { method: 'GET' },
    )
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error ?? `Failed to generate ${type.toUpperCase()} (HTTP ${res.status})`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    a.remove(); URL.revokeObjectURL(url)
  },

  // download the project's Vessel Asset Inventory as an .xlsx
  downloadAssetInventory: async (projectId: string, filename: string): Promise<void> => {
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/projects/${projectId}/asset-inventory/export`, { method: 'GET' })
    if (!res.ok) throw new Error(`Failed to export asset inventory (HTTP ${res.status})`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    a.remove(); URL.revokeObjectURL(url)
  },

  // generate the Word RA and trigger a browser download
  downloadGeneratedRa: async (raDocumentId: string, filename: string): Promise<void> => {
    const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/ra-documents/${raDocumentId}/generate`, { method: 'GET' })
    if (!res.ok) throw new Error(`Failed to generate RA (HTTP ${res.status})`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    a.remove(); URL.revokeObjectURL(url)
  },

  // #3: delete a Word RA document and everything derived from it
  // (assessments, details, diagrams, source, auto-written VAI). mapped
  // project_systems survive, manual inventory survives
  deleteRaDocument: (raDocumentId: string) =>
    apiFetch<{ deleted: boolean; assessments: number; blobs_unlinked: number; word_ra_inventory_removed: number }>(
      'DELETE', `/api/ra-documents/${raDocumentId}`),

  // resume hook: returns the most recent running session for the doc
  // (or null). used by WordRaProjectTab on mount to re-attach the
  // progress modal if the user navigated away mid-run. a stale running
  // session (no updated_at progress for ≥30 min) is auto-failed by the
  // backend before being returned
  getRunningAiSession: (raDocumentId: string) =>
    apiFetch<{
      session: ({ id: string; state: string; ra_document_id: string } & Record<string, any>) | null
      stale_auto_failed?: boolean
    }>('GET', `/api/ra-documents/${raDocumentId}/ai-sessions/running`),

  commitLegacyAssessment: (
    projectSystemId: string,
    body: {
      header: LegacyRaHeader
      row: LegacyRaRow
      source_filename: string
      vai_rows?:          AssetInventoryRow[]
      vai_vessel_header?: VaiPreview['vessel_header']
    },
  ) => apiFetch<LegacyRaAssessment & { vai_deleted?: number; vai_inserted?: number; vai_skipped?: number }>(
    'POST', `/api/project-systems/${projectSystemId}/commit-legacy-assessment`, body,
  ),

  getLegacyAssessment: (projectSystemId: string) =>
    apiFetch<LegacyRaAssessment | null>(
      'GET', `/api/project-systems/${projectSystemId}/legacy-assessment`,
    ),

  deleteLegacyAssessment: (projectSystemId: string) =>
    apiFetch<{ deleted: boolean }>(
      'DELETE', `/api/project-systems/${projectSystemId}/legacy-assessment`,
    ),

  // patch a single systems_190 row via the generic crudRouter PUT-by-id route.
  // crudRouter returns the updated rows as an array, we surface the first row
  // for ergonomic callers. server-side enforces super-admin on writes for
  // systems_190 (see server/src/index.ts CRUD loop)
  updateAssetInventoryRow: async (
    id: string,
    patch: Partial<AssetInventoryRow>,
  ): Promise<AssetInventoryRow | null> => {
    const rows = await apiFetch<AssetInventoryRow[]>('PUT', `/api/systems-190/${id}`, patch)
    return rows[0] ?? null
  },
}
